import React, { useEffect, useState } from 'react';
import Alert from '../../../common/layouts/modal/Alert';
import LoginModal from '../../../common/layouts/modal/LoginModal';
import { getCommentList, registerComment, deleteComment, reportComment, likeComment } from '../../../api';
import CommentInput from './CommentInput';
import CommentItem, { CommentData } from './CommentItem';

interface CommentListProps {
    noteId: string;
    onCommentCountChange?: (count: number) => void;
}

const CommentList: React.FC<CommentListProps> = ({ noteId, onCommentCountChange }) => {
    const [comments, setComments] = useState<CommentData[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [showDeactivatedAlert, setShowDeactivatedAlert] = useState(false);
    const [deactivatedAlertMessage, setDeactivatedAlertMessage] = useState("");

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [commentIdToDelete, setCommentIdToDelete] = useState<string | null>(null);
    const [showConfirmReport, setShowConfirmReport] = useState(false);
    const [commentIdToReport, setCommentIdToReport] = useState<string | null>(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);

    const handleLoginModalClose = () => {
        setShowLoginModal(false);
        if (localStorage.getItem("jwtToken")) {
            fetchComments(0, false);
        }
    };

    const fetchComments = async (pageNum: number = 0, isAppend: boolean = false) => {
        setLoading(true);
        try {
            const response = await getCommentList(noteId, pageNum);
            if (response) {
                const raw: any[] = response.content || response || [];
                const email = localStorage.getItem("email");
                
                // 차단된 댓글 필터링
                const activeComments = raw.filter((item: any) => item.status !== 'BLOCKED');
                
                // 차단된 댓글 개수만큼 총합에서 삭감
                const blockedCount = raw.length - activeComments.length;
                const finalTotalElements = Math.max(0, (response.totalElements || raw.length) - blockedCount);

                const mapped = activeComments.map((item: any) => {
                    const likedKey = `comment_liked_${email || 'anonymous'}_${item.id}`;
                    return {
                        ...item,
                        isLiked: localStorage.getItem(likedKey) === 'true',
                        isMine: item.isMine || item.mine || (email && item.writerEmail === email) || false,
                    };
                });

                if (isAppend) {
                    setComments(prev => [...prev, ...mapped]);
                } else {
                    setComments(mapped);
                }
                setTotalCount(finalTotalElements);
                setHasNext(response.last === false || (response.content && response.content.length === 5));
                setPage(pageNum);
            }
        } catch (error) {
            console.error("Failed to fetch comments", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (noteId) fetchComments(0, false);
    }, [noteId]);

    useEffect(() => {
        if (onCommentCountChange) {
            onCommentCountChange(totalCount);
        }
    }, [totalCount, onCommentCountChange]);

    const handleLoadMore = () => fetchComments(page + 1, true);

    const handleCreateComment = async (content: string) => {
        const currentStatus = localStorage.getItem("authorStatus");
        if (currentStatus && currentStatus.toLowerCase() === "hold") {
            setDeactivatedAlertMessage("현재 계정 이용이 제한된 상태입니다.\n운영 정책 위반으로 인해\n댓글 등록 기능이 제한되었습니다.\n기존 게시글은 수정 후 안내 메일에 따라 재검토 요청이 가능합니다.");
            setShowDeactivatedAlert(true);
            return;
        }
        try {
            await registerComment(noteId, content);
            await fetchComments(0, false);
        } catch (error) {
            alert("댓글 등록에 실패했습니다.");
        }
    };

    const handleReplyComment = async (parentId: string, content: string) => {
        const currentStatus = localStorage.getItem("authorStatus");
        if (currentStatus && currentStatus.toLowerCase() === "hold") {
            setDeactivatedAlertMessage("현재 계정 이용이 제한된 상태입니다.\n운영 정책 위반으로 인해\n댓글 등록 기능이 제한되었습니다.\n기존 게시글은 수정 후 안내 메일에 따라 재검토 요청이 가능합니다.");
            setShowDeactivatedAlert(true);
            return;
        }
        try {
            await registerComment(noteId, content, parentId);
            setTotalCount(prev => prev + 1);
        } catch (error) {
            alert("답글 등록에 실패했습니다.");
            throw error;
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        setCommentIdToDelete(commentId);
        setShowConfirmDelete(true);
    };

    const handleDeleteConfirmExecute = async () => {
        setShowConfirmDelete(false);
        if (!commentIdToDelete) return;
        try {
            await deleteComment(commentIdToDelete);
            setAlertMessage("댓글이 삭제되었습니다.");
            setShowAlert(true);
            await fetchComments(0, false);
        } catch (error) {
            setAlertMessage("댓글 삭제에 실패했습니다.");
            setShowAlert(true);
        } finally {
            setCommentIdToDelete(null);
        }
    };

    const handleReportComment = async (commentId: string) => {
        const token = localStorage.getItem("jwtToken");
        if (!token) {
            setShowLoginModal(true);
            return;
        }
        setCommentIdToReport(commentId);
        setShowConfirmReport(true);
    };

    const handleReportConfirmExecute = async () => {
        setShowConfirmReport(false);
        if (!commentIdToReport) return;
        try {
            await reportComment(commentIdToReport);
            setAlertMessage("신고가 접수되었습니다.");
            setShowAlert(true);
            await fetchComments(0, false);
        } catch (error: any) {
            const errMsg = error.response?.data?.message || error.response?.data || "신고 처리에 실패했습니다.";
            setAlertMessage(errMsg);
            setShowAlert(true);
        } finally {
            setCommentIdToReport(null);
        }
    };

    const handleLikeComment = async (commentId: string) => {
        const currentStatus = localStorage.getItem("authorStatus");
        if (currentStatus && currentStatus.toLowerCase() === "hold") {
            setDeactivatedAlertMessage("현재 계정 이용이 제한된 상태입니다.\n운영 정책 위반으로 인해\n좋아요 기능이 제한되었습니다.\n기존 게시글은 수정 후 안내 메일에 따라 재검토 요청이 가능합니다.");
            setShowDeactivatedAlert(true);
            return;
        }
        try {
            await likeComment(commentId);
            const email = localStorage.getItem("email") || "anonymous";
            const likedKey = `comment_liked_${email}_${commentId}`;
            const isCurrentlyLiked = localStorage.getItem(likedKey) === 'true';
            if (isCurrentlyLiked) {
                localStorage.removeItem(likedKey);
            } else {
                localStorage.setItem(likedKey, 'true');
            }
            
            setComments(prevComments => {
                return prevComments.map(c => {
                    if (c.id === commentId) {
                        return {
                            ...c,
                            isLiked: !isCurrentlyLiked,
                            likeCount: isCurrentlyLiked ? Math.max(0, c.likeCount - 1) : c.likeCount + 1
                        };
                    }
                    if (c.children && c.children.length > 0) {
                        const updatedChildren = c.children.map(child => {
                            if (child.id === commentId) {
                                return {
                                    ...child,
                                    isLiked: !isCurrentlyLiked,
                                    likeCount: isCurrentlyLiked ? Math.max(0, child.likeCount - 1) : child.likeCount + 1
                                };
                            }
                            return child;
                        });
                        return { ...c, children: updatedChildren };
                    }
                    return c;
                });
            });
        } catch (error) {
            console.error("Like failed", error);
        }
    };

    return (
        <div className="bg-white border border-[#e5e7eb] rounded-[8px] overflow-hidden">

            {/* 헤더 */}
            <div className="px-6 py-6 md:px-4 md:py-4 border-b border-[#e5e7eb]">
                <span className="text-[20px] md:text-[14px] font-bold text-[#111827]">
                    댓글{' '}
                    <span className="text-[#19cf7a]">{totalCount}</span>
                </span>
            </div>

            {/* 댓글 입력 */}
            <div className="px-6 py-6 md:px-4 md:py-4 border-b border-[#e5e7eb]">
                <CommentInput onSubmit={handleCreateComment} />
            </div>

            {/* 댓글 목록 */}
            <div className="px-6 md:px-4">
                {loading && page === 0 ? (
                    <div className="py-12 text-center text-[#bdbdbd] text-[14px]">불러오는 중...</div>
                ) : comments.length === 0 ? (
                    <div className="py-12 text-center text-[#bdbdbd] text-[14px]">첫 번째 댓글을 남겨보세요.</div>
                ) : (
                    <div className="divide-y divide-[#e5e7eb]">
                        {comments.map(comment => (
                            <CommentItem
                                key={comment.id}
                                comment={comment}
                                onReply={handleReplyComment}
                                onDelete={handleDeleteComment}
                                onReport={handleReportComment}
                                onLike={handleLikeComment}
                            />
                        ))}
                    </div>
                )}

                {/* 댓글 더 보기 */}
                {hasNext && (
                    <div className="py-5 flex justify-center border-t border-[#e5e7eb]">
                        <button
                            onClick={handleLoadMore}
                            disabled={loading}
                            className="text-[#6b7280] text-[14px] hover:text-[#19cf7a] transition-colors disabled:opacity-50"
                        >
                            {loading ? "로딩 중..." : "댓글 더 보기"}
                        </button>
                    </div>
                )}
            </div>

            {/* 하단 패딩 (더보기 없을 때) */}
            {!hasNext && comments.length > 0 && <div className="pb-4" />}
            {showDeactivatedAlert && (
                <Alert
                    title="알림"
                    message={deactivatedAlertMessage}
                    onClose={() => setShowDeactivatedAlert(false)}
                />
            )}
            {showConfirmDelete && (
                <Alert
                    message="댓글을 삭제하시겠습니까?"
                    showCancel={true}
                    onClose={() => setShowConfirmDelete(false)}
                    onConfirm={handleDeleteConfirmExecute}
                />
            )}
            {showConfirmReport && (
                <Alert
                    message="이 댓글을 신고하시겠습니까?"
                    showCancel={true}
                    onClose={() => setShowConfirmReport(false)}
                    onConfirm={handleReportConfirmExecute}
                />
            )}
            {showAlert && (
                <Alert
                    message={alertMessage}
                    onClose={() => setShowAlert(false)}
                />
            )}
            {showLoginModal && (
                <LoginModal
                    setNickName={() => {}}
                    token=""
                    setToken={() => {}}
                    onClose={handleLoginModalClose}
                    closeModal={handleLoginModalClose}
                />
            )}
        </div>
    );
};

export default CommentList;
